/**
 * 音高识别校准向导（视弹/跟弹）。
 *
 * 用途：不同用户用的琴（真钢/电钢/键盘/手机钢琴 App）音色、谐波结构、麦克风
 * 拾音特性都不同，多音引擎里「命中阈值」不能一刀切。本向导引导用户逐个弹几组
 * 代表性音，实时用 `scoreExpected` 打分，测出「真弹」时的置信度水平，自动算一个
 * 贴合当前设备的命中阈值，随账号存到后端（换设备/浏览器也跟随）。
 *
 * 数据流：宿主（demo）通过 props 注入 `onSave`，本组件只做「开麦 → 打分 → 算阈值
 * → 回调保存」，不 import 任何 saas 模块。
 *
 * 交互（与视弹「一直等待」对齐，不设超时）：
 *   - 每个音/和弦要**连续命中 3 次**才算采完（避免单次误弹或没弹稳）；
 *   - 命中采用「去抖」：一次长按只算 1 次，必须等声音落下（静音）再弹下一次；
 *   - 弹满 3 次后点亮该步，用户点「下一个」进入下一组音；
 *   - 不熟练用户可慢慢找音，向导一直等，不会自动跳到下一步。
 */
import React, { useEffect, useRef, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { midiToName } from './pitch'
import { midiToJianpu } from './_pitchHelpers'
import { RealtimePolyphonicEngine, AnalysisResult } from './pitch/RealtimePolyphonicEngine'
import { describeMicError } from './micError'

export type PitchCalibration = {
	hitThreshold?: number
	inharmonicity?: number
	decay?: number
	calibratedAt?: string
}

/** 校准的音：低/中/高单音 + 两组和弦，覆盖音域与密度边界。 */
const CALIBRATION_STEPS: { label: string; midis: number[] }[] = [
	{ label: '低音 · 单音', midis: [ 48 ] },   // C3
	{ label: '中音 · 单音', midis: [ 60 ] },   // C4
	{ label: '高音 · 单音', midis: [ 72 ] },   // C5
	{ label: '三和弦', midis: [ 60, 64, 67 ] }, // C4+E4+G4
	{ label: '双手和弦', midis: [ 48, 55, 60, 64, 67 ] }, // C3+G3+C4+E4+G4
]

/** 每个音需要连续命中多少次才算采完 */
const HITS_REQUIRED = 3

const useStyles = createUseStyles({
	overlay: {
		position: 'fixed', inset: 0, zIndex: 10000,
		background: 'rgba(0,0,0,.45)',
		display: 'flex', alignItems: 'center', justifyContent: 'center'
	},
	card: {
		width: 460, maxWidth: '92vw', maxHeight: '86vh', overflow: 'auto',
		background: 'var(--sbt-ed-voidary, #FFF)', borderRadius: 10,
		padding: 20, boxShadow: '0 10px 40px rgba(0,0,0,.3)',
		boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 12
	},
	title: { fontSize: 15, fontWeight: 700, margin: 0 },
	desc: { fontSize: 12, color: 'var(--sbt-ed-muted, #555)', margin: 0, lineHeight: 1.5 },
	noteName: {
		fontSize: 34, fontWeight: 800, textAlign: 'center',
		color: 'var(--sbt-ed-accent, #a8443a)', letterSpacing: 1, margin: 0
	},
	sub: { fontSize: 12, color: 'var(--sbt-ed-muted, #555)', textAlign: 'center', margin: 0 },
	status: { fontSize: 13, textAlign: 'center', margin: 0, minHeight: 18 },
	ok: { color: '#2e8b57' },
	bad: { color: '#c0392b' },
	meterWrap: { display: 'flex', flexDirection: 'column', gap: 4 },
	meterTrack: {
		height: 10, borderRadius: 5, background: '#e8e8e8', overflow: 'hidden'
	},
	meterFill: {
		height: '100%', borderRadius: 5, background: '#2e8b57',
		transition: 'width .12s ease'
	},
	/** 命中进度：3 个小圆点，命中一个点亮一个 */
	hits: { display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' },
	hitDot: {
		width: 14, height: 14, borderRadius: 7,
		border: '2px solid #bbb', background: 'transparent',
		transition: 'all .15s ease'
	},
	hitDotOn: { borderColor: '#2e8b57', background: '#2e8b57' },
	row: { display: 'flex', gap: 8, alignItems: 'center' },
	btn: {
		flex: 1, padding: '9px 12px', borderRadius: 6, border: '1px solid #0003',
		background: 'var(--sbt-ed-voidary, #FFF)', cursor: 'pointer', fontSize: 13,
		fontWeight: 600, color: 'inherit'
	},
	btnPrimary: { background: 'var(--sbt-ed-accent, #a8443a)', color: '#fff', border: 'none' },
	btnDisabled: { opacity: 0.45, cursor: 'default' },
	steps: { display: 'flex', gap: 4, justifyContent: 'center' },
	dot: { width: 8, height: 8, borderRadius: 4, background: '#ccc' },
	dotActive: { background: 'var(--sbt-ed-accent, #a8443a)' },
	dotDone: { background: '#2e8b57' }
})

type Phase = 'intro' | 'opening' | 'playing' | 'done'

export function CalibratePanel(props: {
	onClose: () => void
	onSave: (calib: PitchCalibration) => Promise<void> | void
}) {
	const classes = useStyles()
	const [ phase, setPhase ] = useState<Phase>('intro')
	const [ stepIdx, setStepIdx ] = useState(0)
	const [ micError, setMicError ] = useState('')
	const [ liveConf, setLiveConf ] = useState(0)
	const [ hitCount, setHitCount ] = useState(0)
	const [ stepDone, setStepDone ] = useState(false)
	const [ resultThreshold, setResultThreshold ] = useState<number | null>(null)
	const [ saving, setSaving ] = useState(false)

	const engineRef = useRef<RealtimePolyphonicEngine | null>(null)
	// 每个 step 的「真命中」置信度样本（供结算取峰值）
	const samplesRef = useRef<number[]>([])
	// 去抖：上一次回调是否处于「命中」态。命中后须先回静音（hit 空）才允许再次计数，
	// 否则一次长按会被连续的 onResult 回调数成多次。
	const armedRef = useRef(false)

	const step = CALIBRATION_STEPS[stepIdx]

	// 卸载时务必停麦
	useEffect(() => () => { engineRef.current?.stop() }, [])

	async function startMic() {
		setPhase('opening')
		setMicError('')
		try {
			const engine = new RealtimePolyphonicEngine()
			engine.onResult = (r: AnalysisResult) => {
				// 只看「真命中」的置信度：静音时 confidence=0、hit 为空。
				const hitNotes = r.notes.filter(n => r.hit.includes(n.midi))
				const conf = hitNotes.length > 0 ? Math.max(...hitNotes.map(n => n.confidence)) : 0
				setLiveConf(conf)

				const isHit = r.hit.length > 0
				if(isHit) {
					if(conf > 0) samplesRef.current.push(conf)
					// 去抖：只有从「静音→命中」的上升沿才计一次
					if(armedRef.current) {
						armedRef.current = false
						setHitCount(c => c + 1)
					}
				} else {
					// 回到静音，重新武装，允许下一次命中计数
					armedRef.current = true
				}
			}
			await engine.start()
			engineRef.current = engine
			// 进入第一步：设期望、清样本、复位命中计数
			beginStep(0)
		} catch (err: any) {
			setMicError(describeMicError(err))
			setPhase('intro')
		}
	}

	/** 进入第 i 步：设期望音、清样本与计数、等待用户弹奏。 */
	function beginStep(i: number) {
		const engine = engineRef.current
		if(!engine) return
		const s = CALIBRATION_STEPS[i]
		samplesRef.current = []
		armedRef.current = true
		setHitCount(0)
		setStepDone(false)
		setLiveConf(0)
		setPhase('playing')
		engine.setExpected(s.midis)
	}

	/** 命中次数达到要求时，点亮「本步完成」。 */
	useEffect(() => {
		if(phase == 'playing' && hitCount >= HITS_REQUIRED && !stepDone) {
			setStepDone(true)
		}
	}, [hitCount, phase, stepDone])

	/** 用户点「下一个」：结算当前步，进入下一步或整体结算。 */
	function nextStep() {
		// 本步峰值 = 采集到的真命中 confidence 的最大值
		const peak = samplesRef.current.length > 0 ? Math.max(...samplesRef.current) : 0
		samplesRef.current = []
		const peaks = peaksRefFor(stepIdx)
		peaks[stepIdx] = peak
		if(stepIdx + 1 >= CALIBRATION_STEPS.length) {
			computeThreshold()
		} else {
			setStepIdx(stepIdx + 1)
			beginStep(stepIdx + 1)
		}
	}

	// 每步的峰值缓存（数组按 step 下标存）
	const peaksRef = useRef<number[]>([])
	function peaksRefFor(i: number): number[] {
		if(peaksRef.current.length <= i) {
			peaksRef.current.length = i + 1
		}
		return peaksRef.current
	}

	function computeThreshold() {
		const peaks = peaksRef.current
		// 去掉没测到有效弹奏的步：只有「真正命中了期望音」（过了静音门限 + 阈值）
		// 的步骤才有资格提供阈值样本。峰值过低的步 = 那步没弹上/只有底噪，不能拿
		// 它算阈值，否则会把底噪水平当成「真弹」水平，阈值被压到接近 0。
		const valid = peaks.filter(p => typeof p == 'number' && p > 0.05)
		if(valid.length === 0) {
			setResultThreshold(null)
			setPhase('done')
			return
		}
		// 命中阈值 = 最小「真命中」峰值的 0.7 倍：既保证不漏弹，又高于底噪。
		const minHit = Math.min(...valid)
		setResultThreshold(Math.round(minHit * 0.7 * 100) / 100)
		setPhase('done')
	}

	async function save() {
		if(resultThreshold == null) return
		setSaving(true)
		try {
			await props.onSave({
				hitThreshold: resultThreshold,
				inharmonicity: 0.0004,
				decay: 0.55,
				calibratedAt: new Date().toISOString(),
			})
			engineRef.current?.stop()
			props.onClose()
		} finally {
			setSaving(false)
		}
	}

	function cancel() {
		engineRef.current?.stop()
		props.onClose()
	}

	function retry() {
		peaksRef.current = []
		setStepIdx(0)
		setPhase('intro')
	}

	const isLastStep = stepIdx + 1 >= CALIBRATION_STEPS.length

	return <div className={classes.overlay} onClick={cancel}>
		<div className={classes.card} onClick={e => e.stopPropagation()}>
			<p className={classes.title}>音色校准</p>

			{phase == 'intro' && <>
				<p className={classes.desc}>
					你用的琴音色和麦克风可能和默认设定不同，校准能让识别更准。跟着提示弹 5 组音，
					每组连弹 3 次，弹完点「下一个」，不熟练可以慢慢找音。
				</p>
				<div className={classes.row}>
					<button className={classes.btn} onClick={cancel}>取消</button>
					<button className={`${classes.btn} ${classes.btnPrimary}`} onClick={startMic}>开始校准</button>
				</div>
			</>}

			{phase == 'opening' && <p className={classes.status}>正在打开麦克风…</p>}

			{phase == 'playing' && <>
				<div className={classes.steps}>
					{CALIBRATION_STEPS.map((s, i) => <div key={i}
						className={`${classes.dot} ${i < stepIdx ? classes.dotDone : i == stepIdx ? classes.dotActive : ''}`} />)}
				</div>
				{/* 简谱数字为主（大字），音名作小字对照 —— 面向用简谱的用户 */}
				<p className={classes.noteName}>{step.midis.map(m => midiToJianpu(m)).join(' ')}</p>
				<p className={classes.sub}>{step.midis.map(m => midiToName(m)).join(' + ')} · {step.label}</p>
				{/* 命中进度：3 次 */}
				<div className={classes.hits}>
					{[ 0, 1, 2 ].map(k => <div key={k}
						className={`${classes.hitDot} ${k < hitCount ? classes.hitDotOn : ''}`} />)}
					<span className={classes.sub} style={{minWidth: '5em', textAlign: 'left'}}>
						已弹 {Math.min(hitCount, HITS_REQUIRED)}/{HITS_REQUIRED} 次
					</span>
				</div>
				<p className={classes.status}>
					{stepDone
						? <span className={classes.ok}>✓ 完成，可以点「下一个」了</span>
						: (liveConf > 0
							? <span className={classes.ok}>✓ 检测到了，再弹 {HITS_REQUIRED - hitCount} 次</span>
							: '等待弹奏…（不熟练可以慢慢找音）')}
				</p>
				<div className={classes.meterWrap}>
					<div className={classes.meterTrack}>
						<div className={classes.meterFill} style={{ width: `${Math.min(100, liveConf * 100)}%` }} />
					</div>
					<p className={classes.sub}>置信度 {liveConf.toFixed(2)}</p>
				</div>
				<div className={classes.row}>
					<button className={classes.btn} onClick={cancel}>取消</button>
					<button
						className={`${classes.btn} ${classes.btnPrimary} ${stepDone ? '' : classes.btnDisabled}`}
						disabled={!stepDone}
						onClick={nextStep}>
						{isLastStep ? '完成校准' : '下一个'}
					</button>
				</div>
			</>}

			{phase == 'done' && <>
				<p className={classes.noteName} style={{ fontSize: 20, color: resultThreshold != null ? '#2e8b57' : '#c0392b' }}>
					{resultThreshold != null ? `校准完成 · 阈值 ${resultThreshold.toFixed(2)}` : '校准数据不足'}
				</p>
				<p className={classes.desc}>
					{resultThreshold != null
						? '已根据你的琴音色调整识别阈值，之后视弹/跟弹会更准。'
						: '没测到有效弹奏，可能麦克风没声音，请重试。'}
				</p>
				<div className={classes.row}>
					<button className={classes.btn} onClick={retry}>重新校准</button>
					<button className={`${classes.btn} ${classes.btnPrimary} ${resultThreshold == null ? classes.btnDisabled : ''}`}
						disabled={resultThreshold == null || saving} onClick={save}>
						{saving ? '保存中…' : '保存并应用'}
					</button>
				</div>
			</>}

			{micError && <p className={`${classes.status} ${classes.bad}`}>{micError}</p>}
		</div>
	</div>
}
